(() => {
  // Safe when the section asset is emitted more than once on the same page.
  if (customElements.get('ks-marquee-section')) return;
class KsMarqueeSection extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.list = null;
    this.bootObserver = null;
    this.visibilityObserver = null;
    this.resizeObserver = null;
    this.entryObserver = null;
    this.abortController = null;
    this.rebuildTimer = null;
    this.hasBooted = false;
    this.loopGeometry = null;
    this.handleDocumentVisibility = this.handleDocumentVisibility.bind(this);
  }

  connectedCallback() {
    if (this.hasBooted) return;
    this.track = this.querySelector('.ks-marquee-track');
    this.list = this.querySelector('[data-ks-marquee-original]');
    if (!this.track || !this.list) return;

    this.abortController = new AbortController();
    this.setupTouchPause();
    this.setupEntryAnimation();

    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      this.boot();
      return;
    }

    this.bootObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.bootObserver?.disconnect();
        this.bootObserver = null;
        this.boot();
      },
      { rootMargin: '280px 0px' }
    );
    this.bootObserver.observe(this);
  }

  disconnectedCallback() {
    this.cleanup();
  }

  boot() {
    if (this.hasBooted || !this.isConnected || !this.track || !this.list) return;
    this.hasBooted = true;
    this.rebuildLoop();
    this.setupResizeObserver();
    this.setupRuntimeVisibility();
  }

  cleanup() {
    this.bootObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.entryObserver?.disconnect();
    this.bootObserver = null;
    this.visibilityObserver = null;
    this.resizeObserver = null;
    this.entryObserver = null;
    document.removeEventListener('visibilitychange', this.handleDocumentVisibility);
    this.abortController?.abort();
    this.abortController = null;
    if (this.rebuildTimer) window.clearTimeout(this.rebuildTimer);
    this.rebuildTimer = null;
    this.classList.remove('is-touch-paused');
    this.hasBooted = false;
  }

  setupResizeObserver() {
    if (!('ResizeObserver' in window)) return;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.rebuildTimer) window.clearTimeout(this.rebuildTimer);
      this.rebuildTimer = window.setTimeout(() => this.rebuildLoop(), 120);
    });
    this.resizeObserver.observe(this);
    this.resizeObserver.observe(this.list);
  }

  rebuildLoop() {
    if (!this.track || !this.list || !this.isConnected) return;

    const listWidth = this.list.getBoundingClientRect().width;
    const viewportWidth = this.getBoundingClientRect().width || window.innerWidth;
    if (!Number.isFinite(listWidth) || listWidth <= 0) return;

    const clonesNeeded = Math.max(1, Math.ceil(viewportWidth / listWidth) + 1);
    const geometry = `${listWidth}:${viewportWidth}:${clonesNeeded}`;
    if (this.loopGeometry === geometry && this.track.querySelectorAll('[data-ks-marquee-clone]').length === clonesNeeded) {
      this.handleDocumentVisibility();
      return;
    }
    this.track.classList.remove('is-animating');
    this.track.querySelectorAll('[data-ks-marquee-clone]').forEach((node) => node.remove());
    const fragment = document.createDocumentFragment();
    for (let index = 0; index < clonesNeeded; index += 1) {
      const clone = this.list.cloneNode(true);
      clone.removeAttribute('data-ks-marquee-original');
      clone.setAttribute('data-ks-marquee-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      this.sanitizeClone(clone);
      fragment.appendChild(clone);
    }

    this.track.appendChild(fragment);
    this.loopGeometry = geometry;
    this.track.style.setProperty('--ks-marquee-distance', `${listWidth}px`);
    // Restart only after the clone geometry is committed. This avoids a partial first frame.
    void this.track.offsetWidth;
    this.track.classList.add('is-animating');
    this.handleDocumentVisibility();
  }

  sanitizeClone(clone) {
    const nodes = [clone, ...clone.querySelectorAll('*')];
    nodes.forEach((node) => {
      if (node.id) node.removeAttribute('id');
      node.removeAttribute?.('data-shopify-editor-block');
      if (node.matches?.('a, button, input, select, textarea, [tabindex]')) {
        node.setAttribute('tabindex', '-1');
      }
    });
  }

  setupRuntimeVisibility() {
    if (this.dataset.pauseOffscreen !== 'true') return;

    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0.01);
          this.classList.toggle('is-runtime-paused', !visible || document.hidden);
        },
        { threshold: [0, 0.01, 0.2] }
      );
      this.visibilityObserver.observe(this);
    }

    document.addEventListener('visibilitychange', this.handleDocumentVisibility);
    this.handleDocumentVisibility();
  }

  handleDocumentVisibility() {
    if (this.dataset.pauseOffscreen !== 'true') return;
    if (document.hidden) this.classList.add('is-runtime-paused');
    else {
      const rect = this.getBoundingClientRect();
      const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;
      this.classList.toggle('is-runtime-paused', !inViewport);
    }
  }

  setupTouchPause() {
    if (this.dataset.pauseTouch !== 'true' || !this.abortController) return;
    const signal = this.abortController.signal;
    this.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse') return;
        this.classList.add('is-touch-paused');
      },
      { passive: true, signal }
    );
    const resume = () => this.classList.remove('is-touch-paused');
    document.addEventListener('pointerup', resume, { passive: true, signal });
    document.addEventListener('pointercancel', resume, { passive: true, signal });
  }

  setupEntryAnimation() {
    const type = this.dataset.entryAnimation || 'none';
    if (type === 'none') {
      this.classList.add('is-entry-visible');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      requestAnimationFrame(() => this.classList.add('is-entry-visible'));
      return;
    }

    this.entryObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        requestAnimationFrame(() => this.classList.add('is-entry-visible'));
        this.entryObserver?.disconnect();
        this.entryObserver = null;
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    this.entryObserver.observe(this);
  }
}

if (!customElements.get('ks-marquee-section')) {
  customElements.define('ks-marquee-section', KsMarqueeSection);
}

})();
